import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { recipeService } from "~/services/production";
import type {
  RecipeFilters,
  RecipeFormData,
  RecipeIngredientFormData,
} from "~/types/production";

const QUERY_KEY = "recipes";

export function useRecipes(filters?: RecipeFilters) {
  return useQuery({
    queryKey: [QUERY_KEY, filters],
    queryFn: () => recipeService.getAll(filters),
  });
}

export function useRecipe(id: string | undefined) {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () => recipeService.getById(id!),
    enabled: !!id,
  });
}

export function useDefaultRecipeForProduct(productId: string | undefined) {
  return useQuery({
    queryKey: [QUERY_KEY, "default", productId],
    queryFn: () => recipeService.getDefaultForProduct(productId!),
    enabled: !!productId,
  });
}

export function useCreateRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RecipeFormData) => recipeService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useUpdateRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<RecipeFormData> }) =>
      recipeService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.id] });
    },
  });
}

export function useDeleteRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => recipeService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useSetDefaultRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => recipeService.setDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useAddRecipeIngredient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      recipeId,
      ingredient,
    }: {
      recipeId: string;
      ingredient: RecipeIngredientFormData;
    }) => recipeService.addIngredient(recipeId, ingredient),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, variables.recipeId],
      });
    },
  });
}

export function useUpdateRecipeIngredient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      ingredientId,
      data,
    }: {
      ingredientId: string;
      recipeId: string;
      data: Partial<RecipeIngredientFormData>;
    }) => recipeService.updateIngredient(ingredientId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, variables.recipeId],
      });
    },
  });
}

export function useDeleteRecipeIngredient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      ingredientId,
    }: {
      ingredientId: string;
      recipeId: string;
    }) => recipeService.deleteIngredient(ingredientId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, variables.recipeId],
      });
    },
  });
}

export function useRecipeCost(
  recipeId: string | undefined,
  exchangeRate: number
) {
  return useQuery({
    queryKey: [QUERY_KEY, "cost", recipeId, exchangeRate],
    queryFn: () => recipeService.calculateCost(recipeId!, exchangeRate),
    enabled: !!recipeId && exchangeRate > 0,
  });
}
